import * as React from 'react';
import { FLEX_COLUMN, FLEX_ROW } from '../../../layout/flexBox';
import LocalInput from '../../../localization/LocalInput';
import { INPUT } from '../../../forms/styles/input';
import { Str } from '../../../localization/translationKeys';
import { SELECT } from '../../../forms/styles/select';
import Msg from '../../../localization/Msg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { css, SerializedStyles } from '@emotion/core';
import { COLORS } from '../../../globalStyle/colors';
import { Rater, RaterLevel } from './models';
import { FormikValues } from 'formik';
import { Language } from '../../../localization/models';
import * as R from 'ramda';
import LocalDropdownOption from '../../../localization/LocalDropdownOption';
import LocalDiv from '../../../localization/LocalDiv';
import { TextDirection } from '../../../localization/textDirection';
import Spacer from '../../../layout/Spacer';
import { onMobileMedium } from '../../../globalStyle/mediaQueries';
import LocalSelect from '../../../localization/LocalSelect';
import { useMsgText } from '../../../localization/hooks';
import { btnStyle } from '../../../forms/styles/button';


const styles = {
  label:         css`
    font-weight: bold;
  `,
  formContainer: css`
    width: 192px;

    ${onMobileMedium} {
      width: auto;
    }
  `,
  iconRow:       css`
      width: 100%;
      justify-content: flex-end;
      margin-top: 18px;
  `,
  actionIcon:    css`
    font-size: 26px;
    color: ${COLORS.ICON_GREY};
    cursor: pointer;
  `,
  greenIcon: css`
    color: ${COLORS.GREEN};
  `,
  submitBtn:     css`
    color: transparent;
    background-color: transparent;
    border: 0 none;
    padding: 0;
    height: min-content;
  `,
  disabledSaveIcon: css`
    color: ${COLORS.ICON_GREY};
    cursor: default;
  `,
  saveIcon:      css`
    color: ${COLORS.GREEN};
  `,
  input:         css`
    font-size: 12px;
  `,
  errorInput: css`
    border-color: ${COLORS.RED};
  `,
  dropdown:      css`
    font-size: 12px;
  `,
};


type Props = {
  className?: string;
  rater: Rater;
  showNames: boolean;
  approval: boolean;
  inviteStatus: React.ReactNode;
  approvalElements: React.ReactNode;
  setRater: (rater?: Rater) => void;
  cancelSetRater: () => void;
  raterLevels?: RaterLevel[];
  projectLanguages?: Language[];
  formValues: FormikValues;
  iconStyle?: SerializedStyles;
  disableSubmitBtn: boolean;
  emailFieldError: boolean;
};


const renderLevels = (levels: RaterLevel[]) => R.map(
  level => (
    <LocalDropdownOption
      key={level.surveyLevelId}
      value={level.surveyLevelId}
      labelKey={''}
      labelText={level.fullName}
    />),
  levels,
);


const renderLanguages = (languages: Language[]) => R.map(
  lang => (
    <LocalDropdownOption
      key={lang.languageId}
      value={lang.languageId}
      labelKey={''}
      labelText={lang.nativeName}
    />
  ),
  languages,
);


/**
 * RaterEditableCard: a component that
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterEditableCard: React.FC<Props> = props => {
  const saveLabel   = useMsgText(Str.Save);
  const cancelLabel = useMsgText(Str.Cancel);

  return (
  <LocalDiv className={props.className} key={props.rater.raterId}>
    <LocalDiv css={[FLEX_COLUMN, styles.formContainer]}>
      {
        props.showNames && (
          <LocalDiv css={FLEX_COLUMN}>
            <LocalInput
              name='firstName'
              type='text'
              value={props.formValues.firstName}
              css={[INPUT(), styles.input]}
              placeholderTranslation={Str.FirstName}
            />
            <Spacer sizePx={8} />
            <LocalInput
              name='lastName'
              type='text'
              value={props.formValues.lastName}
              css={[INPUT(), styles.input]}
              placeholderTranslation={Str.LastName}
            />
          </LocalDiv>
        )
      }
      <Spacer sizePx={8} />
      <LocalInput
        name='email'
        type='text'
        value={props.formValues.email}
        css={[INPUT(), styles.input,  props.emailFieldError && styles.errorInput]}
        placeholderTranslation={Str.Email}
      />
      <Spacer sizePx={8} />
      {
        !!props.raterLevels && (
          <React.Fragment>
            <LocalSelect
              css={[SELECT, styles.dropdown]}
              name='levelId'
              value={props.formValues.levelId}
              dir='auto'
            >
              {renderLevels(props.raterLevels)}
            </LocalSelect>
            <Spacer sizePx={8} />
          </React.Fragment>
        )
      }
      {
        !!props.projectLanguages && (
          <LocalSelect
            css={[SELECT, styles.dropdown]}
            name='languageId'
            value={props.formValues.languageId}
            dir='auto'
          >
            {renderLanguages(props.projectLanguages)}
          </LocalSelect>
        )
      }
    </LocalDiv>
    <Spacer sizePx={25} />
    <LocalDiv css={FLEX_ROW}><Msg css={styles.label} str={Str.InvitedColon} />
      <Spacer sizePx={8} />
      <LocalDiv>
        {props.inviteStatus}
      </LocalDiv>
      </LocalDiv>
      <LocalDiv css={[FLEX_ROW, styles.iconRow]} forceDirection={TextDirection.LTR}>
        <Spacer sizePx={50} />
        <button css={styles.submitBtn} type='submit' aria-label={saveLabel}>
          <FontAwesomeIcon
            icon={faSave}
            css={[styles.actionIcon, props.disableSubmitBtn ? styles.disabledSaveIcon : styles.greenIcon]}
          />
        </button>
        <Spacer sizePx={50} />
        <button
          type='button'
          css={btnStyle.invisible}
          onClick={props.cancelSetRater}
          aria-label={cancelLabel}
        >
          <FontAwesomeIcon
            icon={faTimes}
            css={[styles.actionIcon, props.iconStyle]}
          />
        </button>
        <Spacer sizePx={50} />
    </LocalDiv>
  </LocalDiv>
  );
};

export default RaterEditableCard;
